/**
 * settings/cards/SubscriptionStatusCard.tsx
 * -----------------------------------------
 * What: Shows current subscription tier and renewal/upgrade controls.
 * Why: Makes account status visible and actionable from Settings.
 */
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSubscription } from '../../../../lib/SubscriptionContext';
import { useTheme } from '../../../../lib/ThemeContext';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function SubscriptionStatusCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();
  const { tier } = useSubscription();

  // Build neon glow only if required
  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? (Platform.OS === 'web'
          ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
          : {
              shadowColor: theme.accent,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 6,
              elevation: 6,
            })
      : {}
  ), [themeType, theme.accent]);

  const cardStyle = [localStyles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  return (
    <View style={cardStyle}> 
      <TouchableOpacity style={[localStyles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[localStyles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Subscription Status</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>
      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />
          <View style={localStyles.detailsRow}>
            <Text style={[localStyles.detailsLabel, { color: theme.text }]}>Status:</Text>
            <Text style={[localStyles.detailsValue, { color: theme.text }]}>{tier === 'premium' ? 'Premium' : 'Free'}</Text>
          </View>
        </>
      )}
    </View>
  );
}

const localStyles = StyleSheet.create({
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  detailsLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  detailsValue: {
    fontSize: 16,
  },
}); 